/**
 * v3.18-1303 — AppErrorBoundary
 *
 * Top-of-tree defensive boundary that keeps the CRM shell alive when
 * any nested modal, dialog, or route throws during render.  It is
 * the LAST line of defence — the primary strategy remains fixing
 * the root cause in each component (see the ComposeAndApprove
 * doPrepare refactor in the same version), but no modal may ever
 * blank the CRM again even if a new latent bug surfaces.
 *
 * Contract:
 *   • Sidebar + top-nav + Ghost console remain rendered.
 *   • The failing route/panel is replaced with a recovery card that
 *     tells the operator their typed work is autosaved and offers
 *     Retry + Refresh actions.
 *   • The exception + component stack are logged to the console for
 *     engineering diagnostics.
 *   • Recovery keeps the current URL so the operator lands back on
 *     the same client / page after refresh.
 */
import { Component } from 'react';

export class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('[AppErrorBoundary] uncaught render exception', error, info);
    this.setState({ info });
    // Surface to any global error hook (Sentry / Datadog / etc.) —
    // ``window.__hub_report_error__`` is a documented extension point.
    if (typeof window !== 'undefined' && typeof window.__hub_report_error__ === 'function') {
      try { window.__hub_report_error__(error, info); } catch { /* noop */ }
    }
  }

  reset = () => this.setState({ error: null, info: null });

  refresh = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    if (this.state.error) {
      const msg = this.state.error?.message || String(this.state.error);
      const stack = this.state.error?.stack || '';
      return (
        <div
          className="min-h-[420px] flex items-center justify-center p-6"
          data-testid="app-error-boundary"
        >
          <div className="max-w-xl w-full border border-amber-300 bg-amber-50 rounded-xl px-6 py-5 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center text-amber-800 font-bold shrink-0">!</div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-amber-900 mb-1">
                  Something went wrong in this panel
                </h2>
                <p className="text-sm text-amber-800 mb-2">
                  The rest of the CRM is still working — your sidebar, active call, and Ghost console are untouched.  Any message or notes you were typing have been auto-saved.
                </p>
                <p className="text-[12px] text-amber-800/80 break-all font-mono border border-amber-200 rounded bg-amber-100/40 px-2 py-1 mb-2">
                  {msg}
                </p>
                {stack && (
                  <details className="text-[10px] text-amber-800/80">
                    <summary className="cursor-pointer">Stack (send to engineering)</summary>
                    <pre className="whitespace-pre-wrap break-words mt-1 max-h-40 overflow-auto">{stack}</pre>
                  </details>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={this.reset}
                    className="text-xs px-3 py-1.5 rounded bg-amber-800 text-white hover:bg-amber-900"
                    data-testid="app-error-retry"
                  >
                    Try Again
                  </button>
                  <button
                    type="button"
                    onClick={this.refresh}
                    className="text-xs px-3 py-1.5 rounded border border-amber-300 text-amber-900 hover:bg-amber-100"
                    data-testid="app-error-refresh"
                  >
                    Reload Page
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
